import React, { ReactNode, useMemo, useRef, useState, useCallback } from 'react';
import './ServerResponsiveTable.scss';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Card } from 'primereact/card';
import { Sidebar } from 'primereact/sidebar';
import { Dropdown } from 'primereact/dropdown';
import { TableFilter } from '../TableFilter/TableFilter';
import { TableRows } from '../TableRows/TableRows';
import { AppliedFilters } from '../AppliedFilters/AppliedFilters';
import { SortMenu } from '../ResponsiveTable/SortMenu';
import {
  TableColumn,
  TableAction,
  SortState,
  SortDirection,
  TableRow,
  UntypedRow,
  FilterValue,
  TableFilters,
  TableFilterOptions,
} from '../../types/responsiveTable';
import { useServerTable } from '../../hooks/useServerTable';
import classNames from 'classnames';
import { useHorizontalOverflow } from '../../hooks/useHorizontalOverflow';

interface Props<T extends TableRow = UntypedRow> {
  endpoint: string;
  columns: TableColumn<T>[];
  options: TableAction[];
  /**
   * Receives `fetchAllForExport`, which fetches every row matching the table's
   * currently applied filters and sort (no pagination), so header actions like
   * the Excel export button can reuse it instead of fetching unfiltered data.
   */
  headerOptions?: (fetchAllForExport: () => Promise<T[]>) => ReactNode;
  onRowClick?: (row: T) => void;
  disabled?: boolean;
  dataTransform?: (data: T[]) => T[];
  refreshInterval?: number;
  /** Opt-in: keeps filters, sort, page and page size in the URL and in the saved view under this id. */
  tableId?: string;
}

interface TableHeaderProps {
  headerOptions?: ReactNode;
  showFilters: boolean;
  onToggleFilters: () => void;
  /** Number of columns with an applied filter: the badge of the filter buttons (none at 0). */
  appliedFilterCount: number;
  onOpenMobileFilters: () => void;
  onOpenSortMenu: () => void;
}

const MemoizedTableHeader = React.memo<TableHeaderProps>(
  ({ headerOptions, showFilters, onToggleFilters, appliedFilterCount, onOpenMobileFilters, onOpenSortMenu }) => {
    const badge = appliedFilterCount > 0 ? String(appliedFilterCount) : undefined;
    const toggleLabel = (!showFilters ? 'Mostrar' : 'Esconder') + ' filtros';
    return (
      <>
        <div className='header-left'>{headerOptions}</div>
        <div className='header-right'>
          <Button
            label={toggleLabel}
            // The name stays the plain label with a count too: the chips row lists the filters for a screen reader
            aria-label={toggleLabel}
            text
            icon={PrimeIcons.FILTER}
            iconPos='right'
            onClick={onToggleFilters}
            className='show-filters-button'
            badge={badge}
            badgeClassName='filters-count-badge'
          />
        </div>
        <div className='header-right-mobile'>
          <Button
            icon={PrimeIcons.FILTER}
            rounded
            className='p-button-secondary filter-button'
            onClick={onOpenMobileFilters}
            aria-label='Filtrar'
            badge={badge}
            badgeClassName='filters-count-badge'
          />
          <Button
            icon={PrimeIcons.SORT_ALT}
            rounded
            className='sort-button p-button-secondary'
            onClick={onOpenSortMenu}
            aria-label='Ordenar'
          />
        </div>
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.showFilters === nextProps.showFilters &&
      prevProps.appliedFilterCount === nextProps.appliedFilterCount &&
      prevProps.headerOptions === nextProps.headerOptions
    );
  },
);

MemoizedTableHeader.displayName = 'MemoizedTableHeader';

interface TableFilterSectionProps<T extends TableRow> {
  visibleColumns: TableColumn<T>[];
  filters: TableFilters;
  data: T[];
  filterOptions?: TableFilterOptions;
  onFilterChange: (key: string, values: FilterValue[]) => void;
  showFilters: boolean;
  sortState: SortState;
  onSort: (field: string) => void;
  showOptions: boolean;
}

/** React.memo drops the row type parameter; this restores it for the JSX call. */
type TableFilterSectionComponent = <T extends TableRow>(props: TableFilterSectionProps<T>) => React.ReactElement;

const MemoizedTableFilterSection = React.memo(
  <T extends TableRow>({
    visibleColumns,
    filters,
    data,
    filterOptions,
    onFilterChange,
    showFilters,
    sortState,
    onSort,
    showOptions,
  }: TableFilterSectionProps<T>) => {
    return (
      <thead>
        <tr>
          <TableFilter
            columns={visibleColumns}
            filters={filters}
            data={data}
            filterOptions={filterOptions}
            onFilterChange={onFilterChange}
            loading={false}
            showFilters={showFilters}
            sortState={sortState}
            onSort={onSort}
          />
          {showOptions && <th className='option'></th>}
        </tr>
      </thead>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.filters === nextProps.filters &&
      prevProps.sortState.field === nextProps.sortState.field &&
      prevProps.sortState.direction === nextProps.sortState.direction &&
      prevProps.showFilters === nextProps.showFilters &&
      prevProps.showOptions === nextProps.showOptions &&
      prevProps.visibleColumns === nextProps.visibleColumns &&
      prevProps.filterOptions === nextProps.filterOptions
    );
  },
) as TableFilterSectionComponent;

interface TableBodySectionProps<T extends TableRow> {
  loading?: boolean;
  data: T[];
  visibleColumns: TableColumn<T>[];
  showOptions: boolean;
  onOptionsClick: (event: React.MouseEvent<HTMLButtonElement>, id: string) => void;
  onRowClick?: (row: T) => void;
  sortState: SortState;
  menuItems: MenuItem[];
  menuRef: React.RefObject<Menu>;
  skeletonRows: number;
  busy: boolean;
  /** The request of the current view failed: the body shows that instead of rows (#421). */
  failed: boolean;
  /** Asks for the current view again, from the error row. */
  onRetry: () => void;
}

type TableBodySectionComponent = <T extends TableRow>(props: TableBodySectionProps<T>) => React.ReactElement;

const MemoizedTableBodySection = React.memo(
  <T extends TableRow>({
    loading,
    data,
    visibleColumns,
    showOptions,
    onOptionsClick,
    onRowClick,
    sortState,
    menuItems,
    menuRef,
    skeletonRows,
    busy,
    failed,
    onRetry,
  }: TableBodySectionProps<T>) => {
    return (
      <tbody>
        {failed || (!loading && data.length === 0) ? (
          <tr>
            <td colSpan={visibleColumns.length + (showOptions ? 1 : 0)} className='empty-state'>
              <div className='empty-state-content' role={failed ? 'alert' : undefined}>
                <i className={failed ? PrimeIcons.EXCLAMATION_TRIANGLE : PrimeIcons.SEARCH}></i>
                <p>{failed ? 'No se pudieron cargar los resultados' : 'No se encontraron resultados'}</p>
                {failed && <Button label='Reintentar' text onClick={onRetry} />}
              </div>
            </td>
          </tr>
        ) : (
          <TableRows
            data={data}
            columns={visibleColumns}
            onOptionsClick={onOptionsClick}
            loading={loading}
            onRowClick={onRowClick}
            showOptions={showOptions}
            sortState={sortState}
            skeletonRows={skeletonRows}
            busy={busy}
          />
        )}
        <Menu model={menuItems} popup ref={menuRef} />
      </tbody>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data &&
      prevProps.loading === nextProps.loading &&
      prevProps.skeletonRows === nextProps.skeletonRows &&
      prevProps.busy === nextProps.busy &&
      prevProps.failed === nextProps.failed &&
      prevProps.onRetry === nextProps.onRetry &&
      prevProps.showOptions === nextProps.showOptions &&
      prevProps.visibleColumns === nextProps.visibleColumns &&
      prevProps.sortState.field === nextProps.sortState.field &&
      prevProps.sortState.direction === nextProps.sortState.direction
    );
  },
) as TableBodySectionComponent;

export const ServerResponsiveTable = <T extends TableRow = UntypedRow>({
  endpoint,
  columns,
  options,
  headerOptions,
  onRowClick,
  disabled = false,
  dataTransform,
  refreshInterval,
  tableId,
}: Props<T>) => {
  const menu = useRef<Menu>(null);
  const [currentId, setCurrentId] = useState<string>('');
  const currentIdRef = useRef<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showSliderFilters, setShowSliderFilters] = useState(false);
  const [sliderFilters, setSliderFilters] = useState<TableFilters>({});

  const onViewRestored = useCallback((view: { filters: TableFilters }) => {
    if (Object.keys(view.filters).length) setShowFilters(true);
  }, []);

  const {
    data: rawData,
    pagination,
    filterOptions,
    isLoading,
    loadFailed,
    filters,
    sortState,
    page,
    pageSize,
    handlePageChange,
    handleFiltersChange,
    handleSortChange,
    retry,
    fetchAllForExport,
  } = useServerTable<T>({
    endpoint,
    refreshInterval,
    tableId,
    columns,
    onViewRestored,
  });

  // Only the first load draws the skeleton. Once the table has rows, or its endpoint has answered (rows or an error), a later
  // request (a filter, the sort, a page) keeps the rows of the last answer and the chips row on screen, marked busy, so the
  // columns and the height of the table do not jump while it loads, and a chip that has focus keeps it (#417).
  const answeredEndpoint = useRef('');
  if (!isLoading && endpoint) answeredEndpoint.current = endpoint;
  const firstLoad = isLoading && !pagination && answeredEndpoint.current !== endpoint;
  const refreshing = isLoading && !firstLoad;

  const resolvedHeaderOptions = useMemo(() => headerOptions?.(fetchAllForExport), [headerOptions, fetchAllForExport]);

  const data = useMemo(() => {
    if (!rawData) return [];
    return dataTransform ? dataTransform(rawData) : rawData;
  }, [rawData, dataTransform]);

  const paddedData = useMemo(() => {
    // An empty answer is not padded, so the body shows the empty state (#119).
    if (!pagination || data.length === 0) return data;

    const currentPageSize = pagination.pageSize;
    const emptyRowsNeeded = Math.max(0, currentPageSize - data.length);

    if (emptyRowsNeeded === 0) return data;

    const emptyRows = Array.from({ length: emptyRowsNeeded }, (_, index) => ({
      _isEmpty: true,
      _emptyId: `empty-${index}`,
    }));

    return [...data, ...emptyRows] as T[];
  }, [data, pagination]);

  const visibleColumns = useMemo(() => {
    return columns.filter((column) => !column.hideColumn);
  }, [columns]);

  const appliedFilterCount = useMemo(() => Object.keys(filters).length, [filters]);

  const items = useMemo(
    () => [
      {
        items: options
          .filter((opt) => {
            return typeof opt.hasPermission === 'function' ? opt.hasPermission(currentId) : opt.hasPermission;
          })
          .map((opt) => ({
            label: typeof opt.label === 'function' ? opt.label(currentId) : opt.label,
            command: () => opt.onClick && opt.onClick(currentIdRef.current),
          })),
      },
    ],
    [options, currentId],
  );

  const showOptions = useMemo(() => {
    return options.some((opt) => {
      if (typeof opt.hasPermission === 'function') {
        return true;
      }
      return !!opt.hasPermission;
    });
  }, [options]);

  const onOptionsClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    event.preventDefault();
    event.stopPropagation();
    currentIdRef.current = id;
    setCurrentId(id);
    if (menu.current) {
      menu.current.toggle(event);
    }
  }, []);

  const handleFilterChange = useCallback(
    (key: string, values: FilterValue[], isSliderFilters = false) => {
      if (isSliderFilters) {
        setSliderFilters((prev) => ({
          ...prev,
          [key]: values,
        }));
      } else {
        const newFilters = { ...filters };

        if (!values || (Array.isArray(values) && values.length === 0)) {
          delete newFilters[key];
        } else {
          newFilters[key] = values;
        }

        handleFiltersChange(newFilters);
      }
    },
    [filters, handleFiltersChange],
  );

  const removeFilter = useCallback((key: string) => handleFilterChange(key, []), [handleFilterChange]);

  const handleSort = useCallback(
    (field: string) => {
      let newDirection: SortDirection;

      if (sortState.field !== field) {
        newDirection = 'asc';
      } else {
        const nextDirection: Record<SortDirection, SortDirection> = {
          none: 'asc',
          asc: 'desc',
          desc: 'none',
        };
        newDirection = nextDirection[sortState.direction];
      }

      handleSortChange({
        field: newDirection === 'none' ? null : field,
        direction: newDirection,
      });
    },
    [sortState, handleSortChange],
  );

  const clearFilters = useCallback(() => {
    handleFiltersChange({});
    setSliderFilters({});
    setShowSliderFilters(false);
  }, [handleFiltersChange]);

  const applySliderFilters = useCallback(() => {
    const newFilters: TableFilters = {};
    Object.entries(sliderFilters).forEach(([key, values]) => {
      if (!Array.isArray(values) || values.length > 0) newFilters[key] = values;
    });
    handleFiltersChange(newFilters);
    setShowSliderFilters(false);
  }, [sliderFilters, handleFiltersChange]);

  const onPageChangeHandler = useCallback(
    (event: PaginatorPageChangeEvent) => {
      const newPage = Math.floor(event.first / event.rows) + 1;
      handlePageChange(newPage, event.rows);
    },
    [handlePageChange],
  );

  const toggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  // The memoized header keeps the first callback it received, so the sidebar reads the latest filters from a ref.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const openMobileFilters = useCallback(() => {
    setSliderFilters(filtersRef.current);
    setShowSliderFilters(true);
  }, []);

  const hideMobileFilters = useCallback(() => {
    setShowSliderFilters(false);
  }, []);

  const openSortMenu = useCallback(() => {
    setShowSortMenu(true);
  }, []);

  const hideSortMenu = useCallback(() => {
    setShowSortMenu(false);
  }, []);

  const body = useRef<HTMLDivElement>(null);
  const [overflow, scrollBoxRef] = useHorizontalOverflow();
  /** After the last chip goes: focus the button that opens the filters, the desktop one unless CSS hides it (jsdom hides nothing). */
  const focusFilterButton = useCallback(() => {
    const buttons = ['.show-filters-button', '.filter-button']
      .map((selector) => body.current?.closest('.responsive-table')?.querySelector<HTMLElement>(selector))
      .filter((button): button is HTMLElement => !!button);
    const shown = buttons.find((button) => button.parentElement && getComputedStyle(button.parentElement).display !== 'none');
    (shown ?? buttons[0])?.focus();
  }, []);

  return (
    <Card
      className={classNames('responsive-table', { disabled })}
      header={
        <MemoizedTableHeader
          headerOptions={resolvedHeaderOptions}
          showFilters={showFilters}
          onToggleFilters={toggleFilters}
          appliedFilterCount={appliedFilterCount}
          onOpenMobileFilters={openMobileFilters}
          onOpenSortMenu={openSortMenu}
        />
      }
    >
      <AppliedFilters
        columns={visibleColumns}
        filters={filters}
        filterOptions={filterOptions}
        data={data}
        onRemove={removeFilter}
        onClear={clearFilters}
        onFocusLost={focusFilterButton}
        loading={firstLoad}
      />
      <div
        ref={body}
        className='table-scroll-frame'
        style={{ position: 'relative' }}
        data-overflow-start={overflow.start || undefined}
        data-overflow-end={overflow.end || undefined}
      >
        <div ref={scrollBoxRef} className='table-scroll' aria-busy={refreshing} style={{ overflowY: 'auto' }}>
          <table>
            <MemoizedTableFilterSection
              visibleColumns={visibleColumns}
              filters={filters}
              data={data}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              showFilters={showFilters}
              sortState={sortState}
              onSort={handleSort}
              showOptions={showOptions}
            />

            <MemoizedTableBodySection
              loading={firstLoad}
              data={paddedData}
              visibleColumns={visibleColumns}
              showOptions={showOptions}
              onOptionsClick={onOptionsClick}
              onRowClick={onRowClick}
              sortState={sortState}
              menuItems={items}
              menuRef={menu}
              skeletonRows={pageSize}
              busy={refreshing}
              failed={loadFailed}
              onRetry={retry}
            />
          </table>
        </div>

        {pagination && !loadFailed && (
          <Paginator
            first={(page - 1) * pageSize}
            rows={pageSize}
            totalRecords={pagination.totalRecords}
            onPageChange={onPageChangeHandler}
            template={{
              layout:
                'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput RowsPerPageDropdown',
              PageLinks: (options) => {
                if (
                  (options.view.startPage === options.page && options.view.startPage !== 0) ||
                  (options.view.endPage === options.page && options.page + 1 !== options.totalPages)
                ) {
                  return <></>;
                }
                return (
                  <button type='button' className={options.className} onClick={options.onClick}>
                    {options.page + 1}
                  </button>
                );
              },
              CurrentPageReport: (options) => {
                return (
                  <small className='current-page-report'>
                    Página {options.currentPage} de {Math.max(options.totalPages, 1)}
                    <br />
                    Filas {options.first} - {options.last} de {options.totalRecords}
                  </small>
                );
              },
              JumpToPageInput: () => {
                return <div className='paginator-responsive-separator'></div>;
              },
              RowsPerPageDropdown: (options) => {
                const dropdownOptions = [
                  { label: 10, value: 10 },
                  { label: 25, value: 25 },
                  { label: 50, value: 50 },
                ];

                return (
                  <div className='rows-per-page-dropdown'>
                    <span>Filas por página:</span>
                    <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                  </div>
                );
              },
            }}
          />
        )}

        {!isLoading && (
          <>
            <Sidebar
              className='filters-sidebar'
              position='right'
              visible={showSliderFilters}
              onHide={hideMobileFilters}
              header={<h3 className='filters-sidebar-title'>Filtros</h3>}
            >
              <Button
                label='Limpiar filtros'
                text
                icon={PrimeIcons.FILTER_SLASH}
                iconPos='right'
                onClick={clearFilters}
                className='clear-filters'
              />
              <div className='filters-sidebar-container'>
                <TableFilter
                  columns={visibleColumns}
                  filters={sliderFilters}
                  data={data}
                  filterOptions={filterOptions}
                  onFilterChange={handleFilterChange}
                  isSlider={true}
                  showFilters={true}
                  sortState={sortState}
                  onSort={handleSort}
                  loading={false}
                />
              </div>
              <div className='apply-filters'>
                <Button label='Aplicar filtros' onClick={applySliderFilters} />
              </div>
            </Sidebar>
            <SortMenu
              visible={showSortMenu}
              onHide={hideSortMenu}
              columns={visibleColumns}
              sortState={sortState}
              onSort={handleSort}
            />
          </>
        )}
      </div>
    </Card>
  );
};
